import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { Card, CardBody, Button, Alert } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";

export default function Ghl() {
  const navigate = useNavigate();
  const isSubUser = Cookies.get("subuser") === "true";

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleConnect = () => {
    const scope =
      "conversations/message.readonly conversations/message.write locations.readonly contacts.readonly contacts.write conversations.readonly conversations.write";
    const baseUrl =
      "https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&redirect_uri=https://blububl.com/setup-1&client_id=677d610573a75c4f9811461e-m7m5izoo&scope=";
    const fullUrl = baseUrl + encodeURIComponent(scope);
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const handleGoToLinkPage = () => {
    navigate(isSubUser ? "/dashboard/api" : "/dashboard/team");
  };

  const handleConnectDevices = () => {
    navigate("/dashboard/your-devices/get-started");
  };

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const token = Cookies.get("token");
        if(!token)
          return;
        const url = `${global.SERVIP}/user/ghl-connection-checklist`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        setChecklist(data.checklist || {});
      } catch (error) {
        console.error("Error fetching GHL checklist:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChecklist();
  }, []);

  const StepCard = ({ title, description, statusKey, children }) => {
    const status = checklist?.[statusKey];
    if (statusKey && !status) return null;

    const done = status?.done === true;

    return (
      <Card className={`mb-4 shadow-sm border-0 ${done ? "bg-light text-muted" : ""}`}>
        <CardBody>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h5 className="mb-2">{title}</h5>
            {statusKey && (
              <span
                style={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: done ? "#28a745" : "#dc3545",
                }}
              >
                {done ? "✔ Done" : "Not done ❌"}
              </span>
            )}
          </div>
          <p className="mb-3">{description}</p>
          {!done && children}
        </CardBody>
      </Card>
    );
  };

  const warnIfGhlConnectedButNotSimmed =
    !isSubUser &&
    checklist?.locations_connected_to_ghl?.done &&
    !checklist?.locations_connected_to_sims?.done;

  return (
    <>
      <Head title="GHL Integration" />
      <Content>
        <div style={{ display: "flex", gap: "2rem", padding: "2rem 1rem", flexWrap: "wrap" }}>

          <div style={{ flex: "1 1 400px", maxWidth: "720px" }}>
            <h2 className="mb-3 text-center">GoHighLevel Integration</h2>
            <p className="text-center mb-4" style={{ fontSize: "1rem", color: "#666" }}>
              Follow the steps below to integrate BluBubl (TextLink iMessage) with GoHighLevel.
            </p>

            {loading && <p className="text-center mb-4">Loading checklist...</p>}

            {warnIfGhlConnectedButNotSimmed && (
              <Alert color="warning" className="mb-4">
                You've connected one or more GHL locations to subaccounts, but none of those are linked to SIM cards yet. Messages won’t be delivered until you assign SIMs.
              </Alert>
            )}

            <StepCard
              title="Step 0: Connect Your Devices"
              description="Set up and connect the devices that will handle sending and receiving SMS. You’ll only need a smartphone with our app installed."
              statusKey="sim_cards_set_up"
            >
              <Button color="primary" onClick={handleConnectDevices}>
                Connect Devices
              </Button>
            </StepCard>

            {!isSubUser && (
              <StepCard
                title="Step 1: Create a Team Member for Each GHL Location"
                description="Each GoHighLevel location should have its own BluBubl (TextLink iMessage) team member. This allows you to assign unique devices and API keys per location."
                statusKey="subaccounts_set_up"
              >
                <Button color="primary" onClick={handleGoToLinkPage}>
                  Manage Team Members
                </Button>
              </StepCard>
            )}

            <StepCard
              title={`Step ${isSubUser ? "1" : "2"}: Link GHL Locations to Subaccounts`}
              description="Ensure each GHL location is connected to a BluBubl (TextLink iMessage) team member via API key."
              statusKey="locations_connected_to_ghl"
            >
              <Button color="primary" onClick={handleConnect}>
                Connect to GHL
              </Button>
              <br />

              <StepCard
                title={`Step ${isSubUser ? "1.1" : "2.1"}: ${
                  isSubUser ? "Get Your API Key" : "Copy API Keys to Complete GHL Linking"
                }`}
                description={
                  isSubUser
                    ? "Copy your API key and paste it into the GHL linking tab to finalize the integration."
                    : "Go to the 'Team' page, copy each subaccount's API key, and paste it into the tab that opened after clicking 'Connect to GHL'."
                }
              >
                <Button color="primary" onClick={handleGoToLinkPage}>
                  {isSubUser ? "Go to My API Key" : "Go to Team"}
                </Button>
              </StepCard>
            </StepCard>

            {!isSubUser && (
              <StepCard
                title="Step 3: Link SIM Cards to GHL Locations"
                description="Assign SIM cards to each subaccount that is connected to a GHL location."
                statusKey="locations_connected_to_sims"
              >
                <Button color="primary" onClick={handleGoToLinkPage}>
                  Go to Team Members
                </Button>
              </StepCard>
            )}

            <StepCard
              title={`Step ${isSubUser ? "2" : "4"}: Set BluBubl (TextLink iMessage) as Conversation Provider`}
              description="In each GHL Location: Settings → Phone Numbers → Advanced Settings → Select 'TextLink SMS' as the provider."
            />

            <StepCard
              title={`Step ${isSubUser ? "3" : "5"}: Verify Setup`}
              description="Double-check number assignments and access permissions in your BluBubl (TextLink iMessage) dashboard."
            />
          </div>
           <div style={{ flex: "1 1 400px", maxWidth: "600px" }}>
            <Card className="mb-4 shadow-sm border-0">
              <CardBody>
                <h4 className="mb-3">Step-by-Step Tutorial</h4>
                <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
                  <iframe
                    src="https://www.youtube.com/embed/Fiv1HMRunDM"
                    title="BluBubl GHL Integration Tutorial"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      border: "0",
                      borderRadius: "8px"
                    }}
                  ></iframe>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </Content>
    </>
  );
}
